import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Youtube, Mail, Shield, Truck, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { categories } from '@/data/categories';
import { toast } from 'sonner';
import { COMPANY, COMPANY_LEGAL_LINE } from '@/config/companyInfo';

export const Footer: React.FC = () => {
  const [email, setEmail] = React.useState('');

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success('Thanks for subscribing! Check your inbox for a welcome discount.');
    setEmail('');
  };

  return (
    <footer className="bg-foreground text-background mt-auto">
      {/* Newsletter section */}
      <div className="bg-primary">
        <div className="container-wide py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-xl font-bold text-primary-foreground">
                Subscribe to our newsletter
              </p>
              <p className="text-primary-foreground">
                Get discounts and news delivered straight to your inbox
              </p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-2 w-full md:w-auto">
              <Input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-background text-foreground min-w-64"
              />
              <Button type="submit" variant="secondary">
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="container-wide py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About */}
          <div>
            <h4 className="text-lg font-bold mb-4">
              <span className="text-primary-foreground">Giz</span><span className="text-background">Too</span>
            </h4>
            <p className="text-background/70 text-sm mb-4">
              Your go-to store for the latest gadgets and electronics.
              Quality products, great prices, fast shipping.
            </p>
            <div className="flex gap-3">
              <a href="https://facebook.com/giztoo" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors" aria-label="Facebook">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://instagram.com/giztoo" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors" aria-label="Instagram">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="https://twitter.com/giztoo" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors" aria-label="Twitter">
                <Twitter className="h-5 w-5" />
              </a>
              <a href="https://youtube.com/@giztoo" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors" aria-label="YouTube">
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Categories */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Categories</h4>
            <ul className="space-y-2 text-sm">
              {categories.map((cat) => (
                <li key={cat.id}>
                  <Link 
                    to={`/catalog/${cat.slug}`} 
                    className="text-background/70 hover:text-primary transition-colors"
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link 
                  to="/deals" 
                   className="text-background/70 hover:text-primary transition-colors"
                >
                  Deals
                </Link>
              </li>
            </ul>
          </div>

          {/* Information */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Information</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/about" className="text-background/70 hover:text-primary transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/shipping" className="text-background/70 hover:text-primary transition-colors">
                  Shipping & Payment
                </Link>
              </li>
              <li>
                <Link to="/returns" className="text-background/70 hover:text-primary transition-colors">
                  Returns
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-background/70 hover:text-primary transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/trust" className="text-background/70 hover:text-primary transition-colors">
                  Trust &amp; Security
                </Link>
              </li>
              <li>
                <Link to="/business-information" className="text-background/70 hover:text-primary transition-colors">
                  Business Information
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-background/70 hover:text-primary transition-colors">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-background/70 hover:text-primary transition-colors">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-background/70 hover:text-primary transition-colors">
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact & Trust */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2 text-background/70">
                <Mail className="h-4 w-4 text-accent-foreground" />
                <a href="mailto:info@giztoo.com" className="hover:text-primary transition-colors">info@giztoo.com</a>
              </li>
              <li className="text-background/70 text-xs">We respond within 24 hours</li>
            </ul>
            <div className="mt-6 pt-4 border-t border-muted-foreground/20 space-y-2 text-xs text-background/70">
              <div className="flex items-center gap-2"><RotateCcw className="h-3.5 w-3.5" /> 30-Day Returns</div>
              <div className="flex items-center gap-2"><Shield className="h-3.5 w-3.5" /> Secure Checkout</div>
              <div className="flex items-center gap-2"><Truck className="h-3.5 w-3.5" /> Free Shipping $35+</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <Separator className="bg-muted-foreground/20" />
      <div className="container-wide py-4 space-y-3">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-background/70">
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
            <p>© 2026 GizToo. All rights reserved.</p>
            <span className="hidden md:inline">|</span>
            <a href={`mailto:${COMPANY.email}`} className="hover:text-primary transition-colors">{COMPANY.email}</a>
          </div>
          <div className="flex items-center gap-4">
            <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/visa.svg" alt="Visa payment method accepted" className="h-6 opacity-60 invert" />
            <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/mastercard.svg" alt="Mastercard payment method accepted" className="h-6 opacity-60 invert" />
          </div>
        </div>
        <p className="text-xs text-background/60 text-center md:text-left">
          {COMPANY_LEGAL_LINE}
        </p>
      </div>
    </footer>
  );
};